import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Folder, FolderOpen, FolderPlus, ChevronRight, ChevronDown, MoreVertical, Edit, Trash2, FolderInput, Loader2, Database, RefreshCw } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import FolderMoveDialog, { folderError } from '@/components/FolderMoveDialog';
import api from '@/lib/api';
import { toast } from 'sonner';

function FolderNode({ folder, level = 0, selectedFolderId, onSelect, onAction, canEdit, canDelete }) {
  const [expanded, setExpanded] = useState(level === 0);
  const selected = selectedFolderId === folder.id;
  const children = folder.children || [];
  return (
    <li>
      <div className={`flex items-center rounded-lg ${selected ? 'bg-primary/10' : 'hover:bg-secondary'}`} style={{ marginLeft: `${Math.min(level, 4) * 12}px` }}>
        {children.length > 0 ? <Button variant="ghost" className="h-11 w-11 shrink-0 p-0" aria-label={`${expanded ? 'Collapse' : 'Expand'} ${folder.name}`} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</Button> : <span className="w-3 shrink-0" />}
        <button type="button" className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded p-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-current={selected ? 'location' : undefined} onClick={() => onSelect(folder.id)} data-testid={`folder-${folder.id}`}>
          {selected ? <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" /> : <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <span className="flex-1 text-sm break-words min-w-0">{folder.name}</span>
          {folder.document_count > 0 && <Badge variant="secondary" className="shrink-0">{folder.document_count}</Badge>}
        </button>
        {canEdit && <DropdownMenu><DropdownMenuTrigger asChild><Button aria-label={`Actions for ${folder.name}`} variant="ghost" className="h-11 w-11 p-0 shrink-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="min-h-11" onSelect={() => onAction('rename', folder)}><Edit className="w-4 h-4 mr-2" />Rename</DropdownMenuItem>
            <DropdownMenuItem className="min-h-11" onSelect={() => onAction('move', folder)}><FolderInput className="w-4 h-4 mr-2" />Move folder</DropdownMenuItem>
            {canDelete && <DropdownMenuItem className="min-h-11 text-destructive-text" onSelect={() => onAction('delete', folder)}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>}
      </div>
      {expanded && children.length > 0 && <ul>{children.map(child => <FolderNode key={child.id} folder={child} level={level + 1} selectedFolderId={selectedFolderId} onSelect={onSelect} onAction={onAction} canEdit={canEdit} canDelete={canDelete} />)}</ul>}
    </li>
  );
}

const FolderTree = forwardRef(function FolderTree({ selectedFolderId, onSelectFolder, canEdit = false, canDelete = false, onChange }, ref) {
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [action, setAction] = useState(null);
  const lastAction = useRef(null);
  // Radix keeps the dialog mounted during its closing animation.
  const displayedAction = action || lastAction.current;
  const [name, setName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const generation = useRef(0);
  const nameInput = useRef(null);
  const actionTrigger = useRef(null);
  const createTrigger = useRef(null);

  const fetchFolders = async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await api.get('/kb/folders');
      if (request !== generation.current) return;
      setFolders(data.folders || []);
      setUnfiledCount(data.unfiled_count || 0);
    } catch {
      if (request === generation.current) setLoadError('Folders could not be loaded. Try again to see the current structure.');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };
  useImperativeHandle(ref, () => ({ refresh: fetchFolders }));
  useEffect(() => { fetchFolders(); return () => { generation.current += 1; }; }, []);

  const flatten = nodes => nodes.flatMap(f => [f, ...flatten(f.children || [])]);
  const parent = flatten(folders).find(f => f.id === selectedFolderId);
  const openAction = (type, folder) => {
    actionTrigger.current = document.activeElement;
    setName(type === 'rename' ? folder.name : '');
    setSaveError('');
    const next = { type, folder, parent: type === 'create' ? parent : null };
    lastAction.current = next;
    setAction(next);
  };
  const changed = () => { onChange?.({ type: action.type, id: action.folder?.id }); fetchFolders(); };
  const save = async event => {
    event?.preventDefault();
    if (pending.current || !action) return;
    const value = name.trim();
    if (action.type !== 'delete' && (!value || value.length > 200 || ['.', '..'].includes(value) || /[/\\]/.test(value) || [...value].some(char => char.charCodeAt(0) < 32))) {
      setSaveError('Use 1 to 200 characters, without slashes or control characters. The name cannot be . or ..');
      nameInput.current?.focus();
      return;
    }
    pending.current = true;
    setSaving(true);
    setSaveError('');
    try {
      if (action.type === 'create') await api.post('/kb/folders', { name: value, parent_id: action.parent?.id || null });
      else if (action.type === 'rename') await api.put(`/kb/folders/${action.folder.id}`, { name: value });
      else await api.delete(`/kb/folders/${action.folder.id}`);
      toast.success(action.type === 'create' ? 'Folder created' : action.type === 'rename' ? 'Folder renamed' : 'Folder deleted');
      changed();
      setAction(null);
    } catch (error) {
      setSaveError(folderError(error, 'The change could not be saved. Try again.'));
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };
  const title = displayedAction?.type === 'delete' ? 'Delete folder' : displayedAction?.type === 'rename' ? 'Rename folder' : 'Create folder';
  const restoreFocus = event => {
    event.preventDefault();
    if (actionTrigger.current?.isConnected) actionTrigger.current.focus();
    else createTrigger.current?.focus();
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <h3 className="text-sm font-semibold">Folders</h3>
        <div className="flex gap-1">
          <Button variant="ghost" className="h-11 w-11 p-0" aria-label="Refresh folders" disabled={loading} onClick={fetchFolders} data-testid="refresh-folders-btn"><RefreshCw className="h-4 w-4" /></Button>
          {canEdit && <Button ref={createTrigger} variant="ghost" className="h-11 w-11 p-0" aria-label="Create folder" disabled={loading || !!loadError} onClick={() => openAction('create')} data-testid="create-folder-btn"><FolderPlus className="h-4 w-4" /></Button>}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1">
        <button type="button" className="flex min-h-11 w-full items-center gap-2 rounded px-3 text-sm hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring" aria-current={!selectedFolderId || selectedFolderId === 'all' ? 'location' : undefined} onClick={() => onSelectFolder(null)} data-testid="folder-all"><Database className="w-4 h-4 shrink-0" />Knowledge Base</button>
        {loading ? <p role="status" className="p-3 text-sm text-muted-foreground">Loading folders...</p> : loadError ? <div className="p-3 space-y-3"><p role="alert" className="text-sm text-destructive-text">{loadError}</p><Button variant="outline" className="min-h-11" onClick={fetchFolders}>Retry loading folders</Button></div> : <>
          <button type="button" className="flex min-h-11 w-full items-center gap-2 rounded px-3 text-sm hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring" aria-current={selectedFolderId === 'unfiled' ? 'location' : undefined} onClick={() => onSelectFolder('unfiled')} data-testid="folder-unfiled"><FolderInput className="w-4 h-4 shrink-0" /><span className="flex-1 text-left">Unfiled</span>{unfiledCount > 0 && <Badge variant="secondary">{unfiledCount}</Badge>}</button>
          <ul aria-label="Knowledge Base folders">{folders.map(folder => <FolderNode key={folder.id} folder={folder} selectedFolderId={selectedFolderId} onSelect={onSelectFolder} onAction={openAction} canEdit={canEdit} canDelete={canDelete} />)}</ul>
          {folders.length === 0 && <p className="p-4 text-sm text-muted-foreground">No folders yet. Documents without a folder appear in Unfiled.</p>}
        </>}
      </div>
      <Dialog open={!!action && action.type !== 'move'} onOpenChange={open => { if (!open && !pending.current) setAction(null); }}>
        <DialogContent className="max-w-sm max-h-[90dvh] overflow-y-auto" closeClassName="h-11 w-11 top-2 right-2 flex items-center justify-center" onCloseAutoFocus={restoreFocus}>
          <DialogHeader className="pr-8"><DialogTitle>{title}</DialogTitle><DialogDescription className="break-words">{displayedAction?.type === 'delete' ? `Delete “${displayedAction.folder.name}”? It must have no documents or subfolders.` : displayedAction?.type === 'create' ? `Location: ${displayedAction.parent?.path || 'Knowledge Base (top level)'}` : `Current location: ${displayedAction?.folder?.path}`}</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {displayedAction?.type !== 'delete' && <div className="space-y-2"><Label htmlFor="folder-name">Folder name</Label><Input id="folder-name" ref={nameInput} maxLength={200} value={name} disabled={saving} aria-invalid={!!saveError} aria-describedby={saveError ? 'folder-save-error' : undefined} onChange={e => { setName(e.target.value); setSaveError(''); }} /></div>}
            {saveError && <p role="alert" id="folder-save-error" className="text-sm text-destructive-text break-words">{saveError}</p>}
            <DialogFooter className="gap-2"><Button type="button" variant="outline" className="min-h-11" disabled={saving} onClick={() => setAction(null)}>Cancel</Button><Button type="submit" variant={displayedAction?.type === 'delete' ? 'destructive' : 'default'} className={displayedAction?.type === 'delete' ? 'min-h-11 bg-destructive-text text-background hover:bg-destructive-text hover:opacity-90' : 'min-h-11'} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{saving ? 'Saving...' : displayedAction?.type === 'delete' ? 'Delete folder' : displayedAction?.type === 'rename' ? 'Save name' : 'Create folder'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <FolderMoveDialog open={action?.type === 'move'} folder={action?.folder} onOpenChange={open => { if (!open) setAction(null); }} onSuccess={changed} />
    </div>
  );
});
export default FolderTree;
